/**
 * Yoink Adventures V2 - Logger
 *
 * Centralized logging with Sentry integration.
 * Sentry is auto-initialized by the loader script in yoink-page.php.
 * Falls back to console if Sentry isn't available.
 *
 * Usage:
 *   import * as logger from './logger.js';
 *   logger.error('[Module] Something failed', error);
 *   logger.warn('[Module] Something suspicious');
 *   logger.info('[Module] Loaded 500 items');
 *   logger.debug('[Module] Verbose details', data);
 *   logger.setUser({ id: '123', email: 'user@example.com' });
 */

/**
 * Check if Sentry is available
 * @returns {boolean}
 */
function hasSentry() {
  return typeof window.Sentry !== 'undefined';
}

/**
 * Extract structured extra context from variadic args.
 * - Error instances are skipped (handled separately).
 * - Plain objects are spread into extra (preserves existing {context} pattern).
 * - Primitives are stored as arg0, arg1, etc.
 */
function buildExtra(args) {
  const extra = {};
  args.forEach((a, i) => {
    if (a instanceof Error) {
      // Handled separately by caller
    } else if (a !== null && typeof a === 'object' && !Array.isArray(a)) {
      Object.assign(extra, a);
    } else {
      extra[`arg${i}`] = a;
    }
  });
  return extra;
}

/**
 * Log an error to Sentry and console
 * @param {string} message - Error description
 * @param {...any} args - Error object and/or additional context
 */
export function error(message, ...args) {
  console.error(message, ...args);

  if (hasSentry()) {
    const err = args.find(a => a instanceof Error);
    const extra = buildExtra(args);

    if (err) {
      window.Sentry.captureException(err, {
        extra: { message, ...extra }
      });
    } else {
      window.Sentry.captureMessage(message, {
        level: 'error',
        extra
      });
    }
  }
}

/**
 * Log a warning to Sentry and console
 * @param {string} message - Warning description
 * @param {...any} args - Additional context
 */
export function warn(message, ...args) {
  console.warn(message, ...args);

  if (hasSentry()) {
    window.Sentry.captureMessage(message, {
      level: 'warning',
      extra: buildExtra(args)
    });
  }
}

/**
 * Log informational message and add Sentry breadcrumb
 * @param {string} message - Info message
 * @param {...any} args - Additional context
 */
export function info(message, ...args) {
  console.log(message, ...args);

  if (hasSentry()) {
    window.Sentry.addBreadcrumb({
      message,
      category: 'info',
      level: 'info',
      data: args.length ? buildExtra(args) : undefined
    });
  }
}

/**
 * Log debug message (dev only, gated on localStorage)
 *
 * Enable via browser console: localStorage.setItem('debug', '1')
 * Disable: localStorage.removeItem('debug')
 *
 * @param {string} message - Debug message
 * @param {...any} args - Additional context
 */
export function debug(message, ...args) {
  try {
    if (localStorage.getItem('debug')) {
      console.log(message, ...args);
    }
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
  }
}

/**
 * Set user context for error reports
 * Call this when user logs in
 * @param {Object} user - User info { id, email, username }
 */
export function setUser(user) {
  if (hasSentry() && typeof window.Sentry.setUser === 'function') {
    window.Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username
    });
  }
}

/**
 * Clear user context
 * Call this when user logs out
 */
export function clearUser() {
  if (hasSentry() && typeof window.Sentry.setUser === 'function') {
    window.Sentry.setUser(null);
  }
}

/**
 * Add breadcrumb for debugging
 * @param {string} message - What happened
 * @param {string} category - Category (e.g., 'navigation', 'user-action')
 * @param {Object} data - Additional data
 */
export function breadcrumb(message, category = 'app', data = {}) {
  if (hasSentry()) {
    window.Sentry.addBreadcrumb({
      message,
      category,
      data,
      level: 'info'
    });
  }
}

/**
 * Check if Sentry is available
 * @returns {boolean}
 */
export function isEnabled() {
  return hasSentry();
}
